import React from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';

export const PricingSection = ({ onOpenPopup }) => {
  const plans = [
    {
      name: "Basic",
      price: "$349",
      fee: "+ Upto Filing Fee",
      features: [
        "Case Review",
        "Case Filing",
        "Direct Hit Search",
        "Trademark Alert",
        "Trademark Secured",
        "100% Satisfaction Guarantee"
      ],
      note: 'Govt. filing fee not included',
      popular: false,
    },
    {
      name: "Standard",
      price: "$399",
      fee: "+ Upto Filing Fee",
      features: [
        "Case Review",
        "Case Filing",
        "Direct Hit Search",
        "Trademark Alert",
        "Trademark Secured",
        "100% Satisfaction Guarantee",
        "Refusal Risk Meter"
      ],
      note: 'Govt. filing fee not included',
      popular: false,
    },
    {
      name: "Deluxe",
      price: "$449",
      fee: "+ Upto Filing Fee",
      features: [
        "Case Review",
        "Case Filing",
        "Direct Hit Search",
        "Trademark Alert",
        "Trademark Secured",
        "100% Satisfaction Guarantee",
        "Refusal Risk Meter",
        "Trademark Monitoring",
        "Complete Documentation",
        "Digital File",
        "Dedicated Case Manager"
      ],
      note: 'Govt. filing fee not included',
      popular: true,
    },
    {
      name: "Platinum",
      price: "$549",
      fee: "+ Upto Filing Fee",
      features: [
        "Case Review",
        "Case Filing",
        "Direct Hit Search",
        "Trademark Alert",
        "Trademark Secured",
        "100% Satisfaction Guarantee",
        "Refusal Risk Meter",
        "Trademark Monitoring",
        "Complete Documentation",
        "Digital File",
        "Dedicated Case Manager",
        "100% Approval Guarantee",
        "Comprehensive Trademark Search Report (Federal & State)",
        "24 Hrs. Expedited Service",
        "Cease & Desist Letter"
      ],
      note: 'Govt. filing fee not included',
      popular: false,
    },
  ];

  return (
    <section className="py-12 md:py-20 bg-gradient-to-b from-white to-slate-50" data-testid="pricing-section">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm font-medium tracking-widest uppercase text-red-700 mb-4 block">
              Transparent Pricing
            </span>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
              Choose Your Protection Plan
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Select the package that best fits your brand protection needs
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative flex flex-col bg-white rounded-3xl p-8 shadow-lg border-2 transition-all duration-300 hover:shadow-2xl ${plan.popular ? 'border-red-600 shadow-red-100' : 'border-slate-100'
                }`}
              data-testid={`pricing-plan-${index}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-red-700 to-red-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Recommended
                  </span>
                </div>
              )}

              <div className="mb-6">
                <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">
                  {plan.name}
                </h3>
                <p className="text-slate-500 text-sm mb-4">{plan.tagline}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                  <span className="text-slate-500">+ Upto Filing Fee</span>
                </div>
              </div>

              <div className="flex-1 mb-6">
                <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                  {plan.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-start gap-3">
                      <div className="bg-red-50 rounded-full p-1 mt-0.5 flex-shrink-0">
                        <Check className="h-3 w-3 text-red-700" />
                      </div>
                      <span className="text-slate-600 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-xs text-slate-500 italic">{plan.note}</p>
                <Button
                  onClick={onOpenPopup}
                  data-testid={`pricing-button-${index}`}
                  className={`w-full h-12 font-semibold rounded-full transition-all duration-300 ${plan.popular
                    ? 'bg-gradient-to-r from-red-700 to-red-600 hover:from-red-800 hover:to-red-700 text-white shadow-lg'
                    : 'bg-white text-slate-900 border-2 border-slate-200 hover:bg-slate-50'
                    }`}
                >
                  Get Started
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
